import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { BarChart3, TrendingUp, Percent, ShieldCheck, Users } from "lucide-react";
import { formatPilotageCurrency, formatPilotageDecimal } from "@/lib/pilotageUi";

const SHORT_MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];

export default function MonthlyExecutiveCharts({
  year,
  selectedMonth,
  objective,
  cockpitData,
  selectedMonthData,
  monthsData = [],
}) {
  const [activeMetric, setActiveMetric] = useState("ca"); // 'ca' | 'margin' | 'etp' | 'headcount'

  const monthlyTargets = objective?.monthly_effective_targets || objective?.monthly_targets || {};
  const pnlSeries = cockpitData?.tables?.pnl || [];

  // Build 12-month dataset
  const chartData = SHORT_MONTHS.map((name, index) => {
    const monthNum = index + 1;
    const monthKey = String(monthNum).padStart(2, "0");
    const monthTargetDoc = monthlyTargets[monthKey] || {};
    const monthInfo = monthsData.find((item) => item.month === monthNum);
    const isClosed = monthInfo?.closed ?? false;
    const isSelected = selectedMonth === monthNum;

    // Find P&L row if available
    const pnlRow = pnlSeries.find((row) => row.month === `${year}-${monthKey}`) || {};

    // 1. Chiffre d'Affaires
    const targetCa = monthTargetDoc.annual_revenue_target ?? null;
    const actualCa = isClosed
      ? (pnlRow.ca_net ?? (isSelected ? selectedMonthData?.realized?.annual_revenue_target : null) ?? null)
      : (pnlRow.ca_net ?? null);

    // 2. Marge
    const targetMargin = monthTargetDoc.margin_target != null
      ? (monthTargetDoc.margin_target <= 1 ? monthTargetDoc.margin_target * 100 : monthTargetDoc.margin_target)
      : null;
    const actualMargin = isClosed
      ? (pnlRow.margin_pct ?? (isSelected && selectedMonthData?.realized?.margin_target != null ? selectedMonthData.realized.margin_target * 100 : null) ?? null)
      : (pnlRow.margin_pct ?? null);

    // 3. ETP Facturés
    const targetEtp = monthTargetDoc.total_annual_billed_etp ?? null;
    const actualEtp = isClosed
      ? (isSelected ? selectedMonthData?.realized?.total_annual_billed_etp : null)
      : null;

    // 4. Effectif Total
    const targetHeadcount = monthTargetDoc.total_resources_end_year ?? null;
    const actualHeadcount = isClosed
      ? (isSelected ? selectedMonthData?.realized?.total_resources_end_year : null)
      : null;

    return {
      monthNum,
      name,
      isClosed,
      isSelected,
      targetCa,
      actualCa,
      targetMargin,
      actualMargin,
      targetEtp,
      actualEtp,
      targetHeadcount,
      actualHeadcount,
    };
  });

  const getMetricConfig = () => {
    switch (activeMetric) {
      case "margin":
        return {
          title: "Évolution de la Marge (%)",
          unit: "%",
          targetKey: "targetMargin",
          actualKey: "actualMargin",
          targetColor: "#94A3B8",
          actualColor: "#10B981",
          formatter: (val) => (val != null ? `${formatPilotageDecimal(val)} %` : "—"),
          yAxisFormatter: (val) => `${val}%`,
        };
      case "etp":
        return {
          title: "Évolution des ETP Facturés (Base 18j/m)",
          unit: "ETP",
          targetKey: "targetEtp",
          actualKey: "actualEtp",
          targetColor: "#94A3B8",
          actualColor: "#A855F7",
          formatter: (val) => (val != null ? `${formatPilotageDecimal(val)} ETP` : "—"),
          yAxisFormatter: (val) => `${val}`,
        };
      case "headcount":
        return {
          title: "Évolution de l'Effectif Total",
          unit: "pers.",
          targetKey: "targetHeadcount",
          actualKey: "actualHeadcount",
          targetColor: "#94A3B8",
          actualColor: "#F59E0B",
          formatter: (val) => (val != null ? `${Math.round(val)} pers.` : "—"),
          yAxisFormatter: (val) => `${val}`,
        };
      case "ca":
      default:
        return {
          title: "Évolution du Chiffre d'Affaires Net (€)",
          unit: "€",
          targetKey: "targetCa",
          actualKey: "actualCa",
          targetColor: "#94A3B8",
          actualColor: "#3B82F6",
          formatter: (val) => (val != null ? formatPilotageCurrency(val) : "—"),
          yAxisFormatter: (val) => `${Math.round(val / 1000)}k`,
        };
    }
  };

  const metricConfig = getMetricConfig();

  return (
    <div
      className="space-y-4 rounded-2xl border border-white/10 bg-slate-900/60 p-4 sm:p-5"
      data-testid="monthly-executive-trend-chart"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-white">
            <BarChart3 className="h-5 w-5 text-lenvia-blue" />
            <span>Trajectoire Mensuelle sur 12 Mois — {year}</span>
          </h3>
          <p className="mt-0.5 text-xs text-slate-400">
            Comparaison entre les objectifs budgétaires et le réalisé / prévisionnel mois par mois.
          </p>
        </div>

        {/* Onglets de sélection métrique */}
        <div className="flex flex-wrap items-center gap-1 rounded-xl border border-white/10 bg-slate-950/80 p-1">
          <button
            type="button"
            onClick={() => setActiveMetric("ca")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition ${
              activeMetric === "ca"
                ? "bg-lenvia-blue text-white shadow"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <TrendingUp className="h-3.5 w-3.5" />
            <span>CA (€)</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveMetric("margin")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition ${
              activeMetric === "margin"
                ? "bg-emerald-600 text-white shadow"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Percent className="h-3.5 w-3.5" />
            <span>Marge (%)</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveMetric("etp")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition ${
              activeMetric === "etp"
                ? "bg-purple-600 text-white shadow"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>ETP</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveMetric("headcount")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition ${
              activeMetric === "headcount"
                ? "bg-amber-600 text-white shadow"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            <span>Effectif</span>
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-400 px-1">
        <span className="font-medium text-slate-300">{metricConfig.title}</span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-slate-500" />
            <span>Objectif</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: metricConfig.actualColor }} />
            <span>Réalisé / Projeté</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm border border-lenvia-blue bg-lenvia-blue/30" />
            <span>Mois sélectionné ({SHORT_MONTHS[selectedMonth - 1]})</span>
          </span>
        </div>
      </div>

      {/* Rendu graphique */}
      <div className="h-[260px] w-full min-w-0 sm:h-[300px]" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
            <XAxis
              dataKey="name"
              stroke="#94A3B8"
              fontSize={11}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="#94A3B8"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickFormatter={metricConfig.yAxisFormatter}
            />
            <Tooltip
              cursor={{ fill: "rgba(255,255,255,0.04)" }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const point = payload[0]?.payload;
                const targetVal = point?.[metricConfig.targetKey];
                const actualVal = point?.[metricConfig.actualKey];
                return (
                  <div className="rounded-xl border border-white/10 bg-slate-950/95 p-3 shadow-xl backdrop-blur-md">
                    <p className="text-xs font-bold text-white mb-1.5">
                      {label} {year} {point?.isClosed ? "(Clôturé)" : "(Prévisionnel)"}
                    </p>
                    <p className="text-xs text-slate-300">
                      Objectif : <span className="font-semibold text-white">{metricConfig.formatter(targetVal)}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-lenvia-blue">
                      {point?.isClosed ? "Réalisé" : "Prévision"} :{" "}
                      <span className="font-semibold text-emerald-400">
                        {metricConfig.formatter(actualVal)}
                      </span>
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey={metricConfig.targetKey} fill="#64748B" radius={[3, 3, 0, 0]} maxBarSize={16} />
            <Bar dataKey={metricConfig.actualKey} radius={[3, 3, 0, 0]} maxBarSize={16}>
              {chartData.map((entry) => (
                <Cell
                  key={`cell-${entry.monthNum}`}
                  fill={
                    entry.isSelected
                      ? "#38BDF8"
                      : entry.isClosed
                      ? metricConfig.actualColor
                      : `${metricConfig.actualColor}88`
                  }
                  stroke={entry.isSelected ? "#FFFFFF" : undefined}
                  strokeWidth={entry.isSelected ? 1.5 : 0}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
